/**
 * 知识图谱模态框组件
 *
 * 用于设定详情和章节编辑器中展示上下文相关的知识图谱：
 * - 大尺寸（xl）模态框布局
 * - 集成 SimpleGraphViewer 组件
 * - 支持设定相关图谱数据加载
 * - 支持章节相关图谱数据加载
 * - 动态标题显示
 * - 支持 ESC 键和关闭按钮关闭
 * - 支持检查和构建知识图谱（使用异步 REST API）
 * - 实时显示构建进度（向量化、实体提取、去重等流程节点）
 * - 支持任务运行时关闭再打开可继续看到持久化的进度更新
 */

import React, { useEffect, useCallback, useState, useRef } from 'react';
import { SimpleGraphViewer } from './SimpleGraphViewer';
import { knowledgeService } from '../../services/knowledge';
import { useAuthStore } from '../../store/authStore';
import type { EpisodeBuildTask } from '../../types/knowledge';
import { BUILD_STAGES } from '../../types/knowledge';

interface EpisodeStatus {
  exists: boolean;
  entity_count?: number;
  edge_count?: number;
  episode_name?: string;
  error?: string;
}

interface KnowledgeGraphModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: number;
  settingId?: number;
  settingName?: string;
  chapterNum?: number;
  chapterId?: number;
}

const CloseIcon: React.FC = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const BuildIcon: React.FC = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
  </svg>
);

const CheckIcon: React.FC = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const LoadingSpinner: React.FC = () => (
  <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
);

const getTaskStorageKey = (workspaceId: number, chapterId?: number, settingId?: number): string => {
  if (chapterId) {
    return `kg_build_task_${workspaceId}_chapter_${chapterId}`;
  }
  if (settingId) {
    return `kg_build_task_${workspaceId}_setting_${settingId}`;
  }
  return '';
};

const isTaskRunning = (status: string): boolean => {
  return ['pending', 'preparing', 'entity_extraction', 'deduplication', 'edge_translation', 'retrying'].includes(status);
};

const StageIndicator: React.FC<{ currentStage: string }> = ({ currentStage }) => {
  return (
    <div className="flex items-center gap-1 mt-2">
      {BUILD_STAGES.map((stage, index) => {
        const isActive = currentStage === stage.key;
        const isCompleted = BUILD_STAGES.findIndex(s => s.key === currentStage) > index;
        return (
          <div key={stage.key} className="flex items-center">
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                isCompleted
                  ? 'bg-green-500 text-white'
                  : isActive
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 text-gray-500'
              }`}
              title={stage.label}
            >
              {isCompleted ? '✓' : index + 1}
            </div>
            {index < BUILD_STAGES.length - 1 && (
              <div className={`w-3 h-0.5 mx-0.5 ${isCompleted ? 'bg-green-500' : 'bg-gray-200'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
};

export const KnowledgeGraphModal: React.FC<KnowledgeGraphModalProps> = ({
  isOpen,
  onClose,
  workspaceId,
  settingId,
  settingName,
  chapterNum,
  chapterId,
}) => {
  const [episodeStatus, setEpisodeStatus] = useState<EpisodeStatus | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const [buildTask, setBuildTask] = useState<EpisodeBuildTask | null>(null);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const staleCountRef = useRef(0);

  const { token } = useAuthStore();

  const STALE_THRESHOLD = 10;

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    staleCountRef.current = 0;
  }, []);

  const startPolling = useCallback((taskId: string) => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    let lastProgress = -1;

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const task = await knowledgeService.getEpisodeBuildProgress(workspaceId, taskId, token ?? undefined);
        setBuildTask(task);

        if (!isTaskRunning(task.status)) {
          stopPolling();

          if (task.status === 'completed') {
            setEpisodeStatus({
              exists: true,
              entity_count: task.entity_count,
              edge_count: task.edge_count,
            });
            setRefreshKey(prev => prev + 1);
            localStorage.removeItem(getTaskStorageKey(workspaceId, chapterId, settingId));
          } else if (task.status === 'failed') {
            setStatusError(task.error_message || '构建失败');
            localStorage.removeItem(getTaskStorageKey(workspaceId, chapterId, settingId));
          }
          return;
        }

        if (task.progress === lastProgress) {
          staleCountRef.current += 1;
          if (staleCountRef.current >= STALE_THRESHOLD) {
            stopPolling();
            setBuildTask(prev => prev ? {
              ...prev,
              status: 'failed',
              stage: 'failed',
              stage_message: '任务可能被中断，请重新构建',
              error_message: '进度长时间未更新，任务可能因服务重启被中断',
            } : prev);
            localStorage.removeItem(getTaskStorageKey(workspaceId, chapterId, settingId));
            return;
          }
        } else {
          staleCountRef.current = 0;
          lastProgress = task.progress;
        }
      } catch (err) {
        console.error('[KnowledgeGraphModal] 查询进度失败:', err);
      }
    }, 10000);
  }, [workspaceId, chapterId, settingId, token, stopPolling]);

  const recoverTaskFromStorage = useCallback(async () => {
    const storageKey = getTaskStorageKey(workspaceId, chapterId, settingId);
    const storedTaskId = localStorage.getItem(storageKey);

    if (!storedTaskId) {
      return;
    }

    try {
      const task = await knowledgeService.getEpisodeBuildProgress(workspaceId, storedTaskId, token ?? undefined);

      if (isTaskRunning(task.status)) {
        setBuildTask(task);
        startPolling(storedTaskId);
      } else {
        localStorage.removeItem(storageKey);

        if (task.status === 'completed') {
          setEpisodeStatus({
            exists: true,
            entity_count: task.entity_count,
            edge_count: task.edge_count,
          });
          setRefreshKey(prev => prev + 1);
        } else if (task.status === 'failed') {
          setStatusError(task.error_message || '构建失败');
        }
      }
    } catch (err) {
      console.error('[KnowledgeGraphModal] 恢复任务失败:', err);
      localStorage.removeItem(storageKey);
    }
  }, [workspaceId, chapterId, settingId, token, startPolling]);

  const getModalTitle = useCallback(() => {
    if (settingName) {
      return `设定：${settingName} 相关图谱`;
    }
    if (chapterNum) {
      return `第 ${chapterNum} 章 相关图谱`;
    }
    return '知识图谱';
  }, [settingName, chapterNum]);

  const checkEpisodeStatus = useCallback(async () => {
    if (!chapterId && !settingId) return;

    setIsChecking(true);
    setStatusError(null);

    try {
      const result = await knowledgeService.checkEpisodeStatus(
        workspaceId,
        chapterId,
        settingId,
        token ?? undefined
      );
      setEpisodeStatus(result);
    } catch (err) {
      console.error('[KnowledgeGraphModal] 检查 Episode 状态失败:', err);
      setStatusError(err instanceof Error ? err.message : '检查失败');
    } finally {
      setIsChecking(false);
    }
  }, [chapterId, settingId, workspaceId, token]);

  const handleBuildKnowledgeGraph = useCallback(async () => {
    if (!chapterId && !settingId) return;
    if (buildTask && isTaskRunning(buildTask.status)) return;

    setStatusError(null);
    setBuildTask(null);

    try {
      const response = await knowledgeService.startEpisodeBuild(
        workspaceId,
        chapterId,
        settingId,
        token ?? undefined
      );

      const storageKey = getTaskStorageKey(workspaceId, chapterId, settingId);
      localStorage.setItem(storageKey, response.task_id);

      setBuildTask({
        task_id: response.task_id,
        workspace_id: workspaceId,
        chapter_id: chapterId,
        setting_id: settingId,
        status: 'pending',
        progress: 0,
        stage: 'pending',
        stage_message: '任务已创建，等待执行',
      });

      startPolling(response.task_id);
    } catch (err) {
      console.error('[KnowledgeGraphModal] 启动构建任务失败:', err);
      setStatusError(err instanceof Error ? err.message : '启动构建失败');
    }
  }, [chapterId, settingId, workspaceId, token, buildTask, startPolling]);

  useEffect(() => {
    if (isOpen && (chapterId || settingId)) {
      recoverTaskFromStorage();
      checkEpisodeStatus();
    }
  }, [isOpen, chapterId, settingId, recoverTaskFromStorage, checkEpisodeStatus]);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const renderStatusBanner = () => {
    if (isChecking) {
      return (
        <div className="px-4 py-2 bg-blue-50 border-b border-blue-200 text-blue-700 text-sm flex items-center gap-2">
          <LoadingSpinner />
          正在检查知识图谱状态...
        </div>
      );
    }

    if (statusError) {
      return (
        <div className="px-4 py-2 bg-red-50 border-b border-red-200 text-red-700 text-sm">
          {statusError}
        </div>
      );
    }

    if (episodeStatus?.exists) {
      return (
        <div className="px-4 py-2 bg-green-50 border-b border-green-200 text-green-700 text-sm flex items-center gap-2">
          <CheckIcon />
          知识图谱已构建，共 {episodeStatus.entity_count || 0} 个实体、{episodeStatus.edge_count || 0} 个关系
        </div>
      );
    }

    if (episodeStatus && !episodeStatus.exists && !buildTask) {
      return (
        <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-amber-700 text-sm">
          尚未构建知识图谱，请点击下方按钮构建
        </div>
      );
    }

    return null;
  };

  const renderProgressBanner = () => {
    if (!buildTask || !isTaskRunning(buildTask.status)) {
      return null;
    }

    return (
      <div className="px-4 py-3 bg-blue-50 border-b border-blue-200">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-blue-700 font-medium">
            {buildTask.stage_message || '正在构建...'}
          </span>
          <span className="text-sm font-medium text-blue-700">
            {buildTask.progress}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div
            className="bg-blue-500 h-2.5 rounded-full transition-all duration-150"
            style={{ width: `${buildTask.progress}%` }}
          />
        </div>
        {buildTask.stage && <StageIndicator currentStage={buildTask.stage} />}
      </div>
    );
  };

  const renderCompletedBanner = () => {
    if (!buildTask || buildTask.status !== 'completed') {
      return null;
    }

    return (
      <div className="px-4 py-2 bg-green-50 border-b border-green-200 text-green-700 text-sm flex items-center gap-2">
        <CheckIcon />
        构建完成，共 {buildTask.entity_count || 0} 个实体、{buildTask.edge_count || 0} 个关系
        （耗时 {Math.round((buildTask.duration_ms || 0) / 1000)} 秒）
      </div>
    );
  };

  if (!isOpen) return null;

  const isBuilding = buildTask && isTaskRunning(buildTask.status);
  const buildCompleted = buildTask && buildTask.status === 'completed';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black bg-opacity-50" />

      <div
        className="relative bg-white rounded-lg shadow-sm w-full max-w-screen-xl mx-4 h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">{getModalTitle()}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="关闭"
          >
            <CloseIcon />
          </button>
        </div>

        {renderProgressBanner()}
        {renderCompletedBanner()}
        {renderStatusBanner()}

        <div className="flex-1 overflow-hidden p-6">
          <SimpleGraphViewer
            key={refreshKey}
            workspaceId={workspaceId}
            height={600}
            chapterNum={chapterNum}
            settingId={settingId}
            className="w-full h-full"
          />
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div>
            {(chapterId || settingId) && (
              <button
                onClick={handleBuildKnowledgeGraph}
                disabled={isBuilding || isChecking || buildCompleted || episodeStatus?.exists}
                className={`px-4 py-2 text-sm rounded-lg transition-all flex items-center gap-2 ${
                  buildCompleted || episodeStatus?.exists
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : isBuilding
                      ? 'bg-blue-400 text-white cursor-wait'
                      : 'bg-blue-500 hover:bg-blue-600 text-white'
                } disabled:opacity-50`}
                title={
                  buildCompleted || episodeStatus?.exists
                    ? '知识图谱已构建'
                    : isBuilding
                      ? '正在构建...'
                      : '构建知识图谱'
                }
              >
                {isBuilding ? (
                  <>
                    <LoadingSpinner />
                    构建中...
                  </>
                ) : (
                  <>
                    <BuildIcon />
                    {buildCompleted || episodeStatus?.exists ? '已构建' : '构建知识图谱'}
                  </>
                )}
              </button>
            )}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeGraphModal;